import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    InputLabel,
    Box,
    TextField
} from "@mui/material";
import { CheckCircle, Close as CloseIcon, EditCalendar } from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import moment from "moment";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { postNoResponse } from "utils/WebService";
import { errorAlert, successAlert, infoAviso, warningAlert } from "utils/Functions";
import { useState, useEffect } from "react";
import Badge from "@mui/material/Badge";
import DescriptionIcon from "@mui/icons-material/Description";
import CircularProgress from "@mui/material/CircularProgress";
import HistoryConsult from "views/history/HistoryConsult";
import { ESCURO } from "assets/coresTema";

export default function ModalDetails({
    open,
    onClose,
    selectedEvent,
    setSelectedEvent,
    onOpenProcess,
    fetchAgenda,
    onOpenHistory
}) {

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [reasonRescheduling, setReasonRescheduling] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [dateError, setDateError] = useState("");
    const [originalDate, setOriginalDate] = useState("");
    const [loadingSuspension, setLoadingSuspension] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [loadingReagendar, setLoadingReagendar] = useState(false);
    const [historyModalConfig, setHistoryModalConfig] = useState(null);

    useEffect(() => {
        if (open && selectedEvent) {
            setOriginalDate((prev) => {
                if (prev) return prev;
                return selectedEvent.prazo?.data || selectedEvent.data || "";
            });
        } else if (!open) {
            setReasonRescheduling("");
            setReasonError("");
            setDateError("");
            setOriginalDate("");
            setLoadingSuspension(false);
            setLoadingStatus(false);
            setLoadingReagendar(false);
        }
    }, [open, selectedEvent]);

    const {
        statusPrazoEnum,
        tipoEventoEnum,
        sistemasEnum,
        tipoPartesEnum
    } = useUser();

    if (!selectedEvent) return null;

    const prazo = selectedEvent.prazo || selectedEvent || {};
    const caso = selectedEvent.prazo?.casoProcessual || selectedEvent.prazo?.processo || selectedEvent.processo || selectedEvent.casoProcessual || {};
    const quantidadeReagendamento = prazo?.quantidadeReagendamento ?? 0;

    const goToProcess = () => {
        const id = caso?.id;
        if (!id) return;

        onOpenProcess(id);
        onClose();
    };

    const goToHistory = () => {
        const id = caso?.id;
        const idDeadLine = prazo?.id;

        if (!id) return;

        if (selectedEvent.tipoEvento === "endSuspensionProcess") {
            setHistoryModalConfig({
                nomeEntidade: "c039", 
                idEntidade: id
            });
            return
        }

        setHistoryModalConfig({
            nomeEntidade: "c046",
            idEntidade: idDeadLine
        });
    };

    const warningSuspension = () => {
        infoAviso(
            "Deseja realmente desmarcar a suspensão dessa parte do processo?",
            async () => {
                await handleToggleSuspension();
            },
            () => {
                return;
            }
        );
    };

    const handleToggleSuspension = async () => {
        setLoadingSuspension(true);

        const body = {
            id: selectedEvent?.pessoaProcesso?.id,
            suspenso: false,
            casoProcessualId: caso?.id,
        };

        postNoResponse("/processo-parte/save", body)
            .then(() => {
                onClose();
                successAlert("Alterações salvas!");
                fetchAgenda();
            })
            .catch((e) => {
                onClose();
                errorAlert(e.message || "Erro ao salvar alterações.");
            }).finally(() => {
                setLoadingSuspension(false);
            });
    }

    const handleSubmit = async () => {
        let hasError = false;

        if (!prazo.data || !prazo.data.toString().trim()) {
            setDateError("Informe a data do reagendamento.");
            hasError = true;
        } else if (originalDate && moment(prazo.data).isSame(moment(originalDate))) {
            setDateError("A nova data deve ser diferente da data original.");
            hasError = true;
        } else {
            setDateError("");
        }

        if (!reasonRescheduling.trim()) {
            setReasonError("Informe o motivo do reagendamento.");
            hasError = true;
        } else {
            setReasonError("");
        }

        if (hasError) {
            return;
        }

        setLoadingReagendar(true);

        const body = {
            id: prazo.id,
            data: prazo.data,
            tipoPrazo: prazo.tipoPrazo,
            statusPrazo: prazo.statusPrazo,
            processo: {
                id: caso?.id
            },
            motivoReagendamento: reasonRescheduling,
        };

        postNoResponse("/prazo/save", body)
            .then(() => {
                onClose();
                successAlert("Alterações salvas!");
                fetchAgenda();
            })
            .catch((e) => {
                onClose();
                const isLimitError = e.message && (
                    e.message.toLowerCase().includes("limite máximo") || 
                    e.message.toLowerCase().includes("limite maximo") ||
                    e.message.toLowerCase().includes("reagendamentos")
                );
                if (isLimitError) {
                    warningAlert("Máximo de Agendamento permitido.");
                } else {
                    errorAlert(e.message || "Erro ao salvar alterações.");
                }
            }).finally(() => {
                setLoadingReagendar(false);
            });
    };

    const warningStatus = () => {
        infoAviso(
            "Deseja realmente alterar o status?",
            async () => {
                await handleToggleStatus();
            },
            () => {
                return;
            }
        );
    };

    const handleToggleStatus = async () => {
        if (!selectedEvent?.prazo?.id) return;
        setLoadingStatus(true);

        const newStatus = prazo.statusPrazo === "pending" ? "finished" : "pending";

        const body = {
            id: prazo.id,
            data: prazo.data,
            tipoPrazo: prazo.tipoPrazo,
            statusPrazo: newStatus,
            processo: {
                id: caso.id
            }
        };

        postNoResponse("/prazo/save", body)
            .then(() => {
                successAlert("Status atualizado!");
                fetchAgenda();
                onClose();
            })
            .catch((e) => {
                errorAlert(e.message || "Erro ao atualizar status.");
            }).finally(() => {
                setLoadingStatus(false);
            });
    };

    // "Fim de Suspensão" → RENDER ESPECIAL
    if (selectedEvent.tipoEvento === "endSuspension") {
        const p = selectedEvent.processoParte || {};

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: 1000 }}>
                <Box
                    sx={{
                        backgroundColor: darkMode ? ESCURO.cartao : "#fff",
                        color: darkMode ? "#fff" : "#000",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: darkMode ? "#fff" : "#000",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <DialogTitle sx={{ pr: 6 }}>
                        Suspensão
                    </DialogTitle>

                    <DialogContent dividers>
                        <Typography variant="body2">
                            <b>Processo:</b> {caso.processo || "—"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Cliente:</b> {caso.cliente || "—"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Parte Contrária:</b> {caso.parteContraria || "—"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Sistema:</b> {sistemasEnum.find(t => t.code === caso.processoEletronico)?.name || "-"}
                        </Typography>

                        <Typography variant="body2" sx={{ mt: 2 }}>
                            <b>Nome:</b> {p.nome || "—"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Documento:</b> {p.documento || "—"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Motivo:</b> {p.motivo || "—"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Data Início da Suspensão:</b> {p.dataSuspensao ? moment(p.dataSuspensao).format("DD/MM/YYYY") : "-"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Data Fim da Suspensão:</b> {p.dataFimSuspensao ? moment(p.dataFimSuspensao).format("DD/MM/YYYY") : "-"}
                        </Typography>

                        <Typography variant="body2">
                            <b>Tipo Parte:</b> {tipoPartesEnum.find(t => t.code === p.tipoParte)?.name || "-"}
                        </Typography>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <MDButton
                            variant="contained"
                            color="success"
                            onClick={warningSuspension}
                            disabled={loadingSuspension}
                            startIcon={
                                loadingSuspension ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <CheckCircle />
                                )
                            }
                        >
                            Remover Suspensão
                        </MDButton>

                        <MDButton
                            variant="contained"
                            color="info"
                            onClick={goToProcess}
                        >
                            Ir para Processo
                        </MDButton>
                    </DialogActions>
                </Box>
            </Dialog>
        );
    }

    // CASO SEJA PRAZO (deadline) ou SUSPENSÃO NORMAL
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: 1000 }}>
            <Box
                sx={{
                    backgroundColor: darkMode ? ESCURO.cartao : "#fff",
                    color: darkMode ? "#fff" : "#000",
                    position: "relative",
                }}
            >
                {historyModalConfig && (
                    <Dialog open={!!historyModalConfig} onClose={() => setHistoryModalConfig(null)} fullWidth maxWidth="md" sx={{ zIndex: 2000 }}>
                        <DialogTitle sx={{ pb: 1 }}>
                            Histórico do Evento
                            <IconButton onClick={() => setHistoryModalConfig(null)} sx={{ position: "absolute", top: 8, right: 8 }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ backgroundColor: darkMode ? ESCURO.cartao : "#fff", p: 1 }}>
                            <HistoryConsult
                                nomeEntidade={historyModalConfig.nomeEntidade}
                                idEntidade={historyModalConfig.idEntidade}
                            />
                        </DialogContent>
                    </Dialog>
                )}

                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: darkMode ? "#fff" : "#000",
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogTitle sx={{ pr: 6 }}>
                    {selectedEvent.tipoEvento !== "endSuspensionProcess"
                        ? "Detalhes do Evento"
                        : "Detalhes da Suspensão"}
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2">
                        <b>Processo:</b> {caso.processo || "—"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Cliente:</b> {caso.cliente || "—"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Parte Contrária:</b> {caso.parteContraria || "—"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Sistema:</b> {sistemasEnum.find(t => t.code === caso.processoEletronico)?.name || "-"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Data:</b> {moment(selectedEvent.data).format("DD/MM/YYYY HH:mm")}
                    </Typography>

                    <Typography variant="body2">
                        <b>Tipo:</b> {tipoEventoEnum.find(t => t.code === (prazo.tipoPrazo || selectedEvent.tipoEvento))?.name || "-"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Status:</b> {statusPrazoEnum.find(t => t.code === (prazo.statusPrazo || selectedEvent.status))?.name || "-"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Atribuído a:</b> {selectedEvent.pessoa?.nome || prazo.pessoa?.nome || "—"}
                    </Typography>

                    <Typography variant="body2">
                        <b>Descrição:</b> {prazo.descricao || selectedEvent.descricao || "—"}
                    </Typography>

                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={goToHistory}
                        sx={{ mt: 2 }}
                        endIcon={<Badge
                            badgeContent={prazo?.quantidadeReagendamento}
                            color="error"
                            invisible={quantidadeReagendamento === 0}
                            sx={{
                                "& .MuiBadge-badge": {
                                    minWidth: 18,
                                    height: 18,
                                    fontSize: "0.7rem",
                                    top: -2,
                                    right: -2,
                                },
                            }}
                        >
                            <DescriptionIcon />
                        </Badge>}
                    >
                        Histórico
                    </MDButton>

                    <InputLabel sx={{ px: 1, pb: 0.5, mt: 2 }}>Nova Data</InputLabel>
                    <MDDatePicker
                        value={prazo.data ? moment(prazo.data).format("YYYY-MM-DDTHH:mm") : ""}
                        onChange={(dates, dateStr) => {
                            setDateError("");
                            setSelectedEvent((prev) => ({
                                ...prev,
                                prazo: { ...prev.prazo, data: dateStr }
                            }));
                        }}
                        options={{
                            enableTime: true,
                            dateFormat: "Y-m-d\\TH:i",
                            minuteIncrement: 15,
                            time_24hr: true,
                            disableMobile: true,
                            onOpen: function(selectedDates, dateStr, instance) {
                                if (instance && instance.calendarContainer) {
                                    instance.calendarContainer.style.transform = 'scale(0.85)';
                                    instance.calendarContainer.style.transformOrigin = 'top center';
                                }
                            }
                        }}
                        input={{
                            name: "data",
                            fullWidth: true,
                            error: !!dateError,
                            helperText: dateError,
                            InputLabelProps: { shrink: true }
                        }}
                    />

                    <InputLabel sx={{ px: 1, pb: 0.5, mt: 2 }}>Motivo do Reagendamento</InputLabel>
                    <TextField
                        multiline
                        minRows={2}
                        fullWidth
                        placeholder="Descreva o motivo"
                        value={reasonRescheduling}
                        onChange={(e) => {
                            setReasonError("");
                            setReasonRescheduling(e.target.value);
                        }}
                        error={!!reasonError}
                        helperText={reasonError}
                    />

                </DialogContent>

                <DialogActions sx={{ justifyContent: "flex-end", gap: 1, p: 2 }}>

                    <MDButton
                        variant="contained"
                        color="success"
                        onClick={warningStatus}
                        disabled={loadingStatus}
                        startIcon={
                            loadingStatus ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <CheckCircle />
                            )
                        }
                    >
                        {prazo.statusPrazo === "pending" ? "Marcar como Finalizado" : "Marcar como Pendente"}
                    </MDButton>


                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={handleSubmit}
                        disabled={loadingReagendar}
                        startIcon={
                            loadingReagendar ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <EditCalendar />
                            )
                        }
                    >
                        Reagendar
                    </MDButton>


                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={goToProcess}
                    >
                        Ir para Processo
                    </MDButton>
                </DialogActions>

            </Box>
        </Dialog>
    );
}
